import { MutationTree } from 'vuex'
import { MapState } from './types'
import { Thing } from '@/helpers/objects'
import MarkerWithLabel from '@google/markerwithlabel'
import RippleMarker from '@/helpers/maps/pondRipple'
import PondRippleEvent from '@/helpers/maps/pondRippleEvent'
import store from '@/store'
import Vue from 'vue'
import { MapsHelpers } from '@/helpers/mainExports'
import { firebaseServices } from '@leapsight/styleguide'
import { FirebaseTagNames } from '@leapsight/portal'
declare const google: any

export const mutations: MutationTree<MapState> = {
  setMap(state, payload) {
    state.map = payload
  },
  initMapState(state, payload: MapState) {
    for (const [k, v] of Object.entries(payload)) {
      Vue.set(state, k, v)
    }
  },
  showThingsByServicesActive(state) {
    Object.keys(state.things).forEach(key => {
      //Las entidades que están dadas de baja y las morosas en el pago del servicio de live tracking no tienen marker y, por lo tanto, no se actúa sobre este
      const entity = state.things[key]
      const liveTrackingStatus =
        entity?.services?.['mrn:service:live_tracking']?.status
      const servicePlanStatus = entity?.servicePlan?.['status']

      if (
        liveTrackingStatus === 'inactive' ||
        liveTrackingStatus === 'suspended' ||
        servicePlanStatus === 'inactive'
      ) {
        store.dispatch('mapState/destroyMarkerClusterOnMap')
        store.commit('uiState/toggleClusterCreated', false)

        if (entity.marker?.setVisible) {
          entity.marker.setVisible(false)
        }
        Vue.set(entity, 'marker', {})
      }
    })
  },
  cleanMapState(state) {
    Object.keys(state.things).forEach(key => {
      const entity = state.things[key]
      entity.marker?.setMap ? entity.marker.setMap(null) : null
      state.entitiesFiltered[key].marker.setMap
        ? state.entitiesFiltered[key].marker.setMap(null)
        : null
    })
    state.things = {}
    state.entitiesFiltered = {}
  },
  setLabel(state, payload) {
    // Las entidades que están dadas de baja y las morosas en el pago del servicio de live tracking no tienen marker y, por lo tanto, no se actúa sobre este
    const entity = state.things[payload.id]
    const liveTrackingStatus =
      entity?.services?.['mrn:service:live_tracking']?.status
    const servicePlanStatus = entity?.servicePlan?.['status']

    if (
      liveTrackingStatus !== 'inactive' &&
      liveTrackingStatus !== 'suspended' &&
      servicePlanStatus !== 'inactive'
    ) {
      if (entity.marker?.set) {
        entity.marker.set('labelVisible', true)
      }

      // Al mostrar los labels, eliminamos los listeners de la UI
      if (entity.marker) {
        google.maps.event.clearListeners(entity.marker, 'mouseover')
        google.maps.event.clearListeners(entity.marker, 'mouseout')
      }
    }
  },
  setThingsToFollow(state, payload) {
    state.thingsToFollow = payload
  },

  updateLabel(state, payload) {
    state.things[payload.id].marker.set('labelVisible', true)
  },
  removeLabel(state, payload) {
    // Las entidades que están dadas de baja y las morosas en el pago del servicio de live tracking no tienen marker y, por lo tanto, no se actúa sobre este
    if (
      state.things[payload.id]?.services?.['mrn:service:live_tracking']
        ?.status !== 'suspended' &&
      state.things[payload.id]?.services?.['mrn:service:live_tracking']
        ?.status !== 'inactive' &&
      state.things[payload.id]?.servicePlan?.['status'] !== 'inactive'
    ) {
      const markerClass = state.things[payload.id].marker.get('labelClass')
      //En caso de que haya un label seleccionado este no se remueve
      if (!markerClass.includes('selected')) {
        state.things[payload.id].marker.set('labelVisible', false)
        //Seteo listener para mostrar label en el hover
        state.things[payload.id].marker.addListener('mouseover', () => {
          state.things[payload.id].marker.set('labelVisible', true)
        })
        state.things[payload.id].marker.addListener('mouseout', () => {
          state.things[payload.id].marker.set('labelVisible', false)
        })
      }
    }
  },

  resetMarker(state, payload) {
    const thing = payload.thing
    const showEntityName = payload.showEntityName

    state.things[thing.id].marker.set('labelClass', 'gmap__label')
    state.things[thing.id].marker.set('zIndex')

    if (!showEntityName) {
      state.things[thing.id].marker.addListener('mouseout', () => {
        state.things[thing.id].marker.set('labelVisible', false)
      })
      state.things[thing.id].marker.addListener('mouseover', () => {
        state.things[thing.id].marker.set('labelVisible', true)
      })
    }
    state.things[thing.id].marker.set('labelVisible', showEntityName)
  },

  destroyMarkerOnMap(state, payload) {
    if (state.things[payload.id].markerClickListener) {
      google.maps.event.removeListener(
        state.things[payload.id].markerClickListener
      )
      delete state.things[payload.id].markerClickListener
    }
    if (state.things[payload.id].markerMouseoutListener) {
      google.maps.event.removeListener(
        state.things[payload.id].markerMouseoutListener
      )
      delete state.things[payload.id].markerMouseoutListener
    }
    if (state.things[payload.id].markerMouseoverListener) {
      google.maps.event.removeListener(
        state.things[payload.id].markerMouseoverListener
      )
      delete state.things[payload.id].markerMouseoverListener
    }
    if (state.things[payload.id].marker) {
      state.things[payload.id].marker.setMap(null)
    }
    state.things[payload.id].marker = null
  },

  setMarker(
    state,
    { id, showEntityType, icon, position, map, label, showEntityName }
  ) {
    // Calculate label anchor based on entity type and icon size
    const labelAnchor = MapsHelpers.getLabelAnchor(showEntityType, icon)

    // Define the marker image options
    const image = {
      url: icon,
      size: new google.maps.Size(64, 64),
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(32, 32)
    }

    // Create the marker with label
    const marker = new MarkerWithLabel({
      position,
      map,
      icon: image,
      labelContent: label,
      labelAnchor,
      labelClass: 'gmap__label',
      labelStyle: { opacity: 0.85 },
      labelVisible: showEntityName
    })

    // Store the marker instance
    state.things[id].marker = marker

    // Add click listener to the marker
    const clickListener = marker.addListener('click', () => {
      firebaseServices.logEvent(
        firebaseServices.analytics,
        FirebaseTagNames.BTN_LIVE_TRACKING_ENTITY_MODAL
      )

      store.dispatch('uiState/closeLabelSelected')

      // Update label appearance on click
      marker.set('labelClass', 'gmap__label selected')
      marker.set('zIndex', 10000)
      marker.set('labelVisible', true)

      // Remove mouseout listener (if it exists)
      if (state.things[id].markerMouseoutListener) {
        google.maps.event.removeListener(
          state.things[id].markerMouseoutListener
        )
        delete state.things[id].markerMouseoutListener
      }

      // Dispatch action to open the ThingInfoWindow
      store.dispatch('uiState/openThingInfo', state.things[id])
    })

    // Store the click listener reference (though it might not be used for removal)
    state.things[id].markerClickListener = clickListener

    // Add mouseover/mouseout listeners if entity name is not always shown
    if (!showEntityName) {
      const mouseoverListener = marker.addListener('mouseover', () => {
        marker.set('labelVisible', true)
      })
      state.things[id].markerMouseoverListener = mouseoverListener

      const mouseoutListener = marker.addListener('mouseout', () => {
        marker.set('labelVisible', false)
      })
      state.things[id].markerMouseoutListener = mouseoutListener
    } else {
      // If the label is always visible, ensure mouseover/mouseout listeners are removed
      if (state.things[id].markerMouseoverListener) {
        google.maps.event.removeListener(
          state.things[id].markerMouseoverListener
        )
        delete state.things[id].markerMouseoverListener
      }
      if (state.things[id].markerMouseoutListener) {
        google.maps.event.removeListener(
          state.things[id].markerMouseoutListener
        )
        delete state.things[id].markerMouseoutListener
      }
    }
  },
  updateMarker(state, payload) {
    const id = payload.id
    const markerToUpdate = state.things[id].marker

    if (markerToUpdate?.setPosition) {
      const image = {
        url: payload.icon,
        size: new google.maps.Size(64, 64),
        origin: new google.maps.Point(0, 0),
        anchor: new google.maps.Point(32, 32)
      }
      markerToUpdate.setPosition(payload.position)
      markerToUpdate.setIcon(image)
      markerToUpdate.set('labelContent', payload.label)
      const labelAnchor = MapsHelpers.getLabelAnchor(
        payload.showEntityType,
        payload.icon
      )
      markerToUpdate.set('labelAnchor', labelAnchor)
      if (store.state.markerCluster && store.getters['uiState/showCluster']) {
        store.state.markerCluster.removeMarker(markerToUpdate, false)
        store.state.markerCluster.addMarker(markerToUpdate, true)
      }
    }
  },
  toggleMarkerType(state, payload) {
    // Las entidades que están dadas de baja y las morosas en el pago del servicio de live tracking no tienen marker y, por lo tanto, no se actúa sobre este
    if (
      state.things[payload.id]?.services['mrn:service:live_tracking']
        ?.status !== 'suspended' &&
      state.things[payload.id]?.servicePlan['status'] !== 'inactive'
    ) {
      const image = {
        url: payload.icon,
        size: new google.maps.Size(64, 64),
        origin: new google.maps.Point(0, 0),
        anchor: new google.maps.Point(32, 32)
      }
      state.things[payload.id]?.marker.setIcon(image)
      const labelAnchor = MapsHelpers.getLabelAnchor(
        payload.showEntityType,
        payload.icon
      )
      state.things[payload.id]?.marker.set('labelAnchor', labelAnchor)
    }
  },
  newThing(state, dataThing) {
    const newThing = new Thing({
      id: dataThing.id,
      name: dataThing.name,
      isAlias: dataThing.isAlias,
      domain: dataThing.domain,
      tags: dataThing.tags,
      status: dataThing.status,
      position: dataThing.position,
      heading: dataThing.heading,
      lastReport: dataThing.lastReport,
      type: dataThing.type,
      subType: dataThing.subType,
      speed: dataThing.speed,
      speedLastModified: dataThing.speedLastModified,
      servicePlan: dataThing.servicePlan,
      services: dataThing.services,
      ...(dataThing.type == 'flex' && { batteryLevel: dataThing.batteryLevel }),
      ...(dataThing.type !== 'flex' && {
        batteryStatus: dataThing.batteryStatus,
        thing_contact_on: dataThing?.thing_contact_on,
        ignition_status: dataThing?.ignition_status,
        contact_on: dataThing.contact_on,
        fuel_cut_off: dataThing.fuel_cut_off
      }),
      gpsId: dataThing.gpsId,
      fuel_tank: dataThing.fuel_tank,
      rpm_engine: dataThing.rpm_engine,
      telemetry: dataThing.telemetry,
      created_timestamp: dataThing.created_timestamp,
      sensors: dataThing.sensors,
      signalSensorTimes: dataThing.signalSensorTimes,
      ibutton: dataThing.ibutton
    })

    Vue.set(state.things, dataThing.id, newThing)
    Vue.set(state.entitiesFiltered, dataThing.id, newThing)
  },
  startTime(state) {
    state.payloadIsEmpty = true
  },
  stopTime(state) {
    state.payloadIsEmpty = false
  },
  updateThing(state, { dataThing }) {
    state.things[dataThing.id].name = dataThing.name
    state.things[dataThing.id].domain = dataThing.domain
    state.things[dataThing.id].tags = dataThing.tags
    state.things[dataThing.id].status = dataThing.status
    state.things[dataThing.id].position = dataThing.position
    state.things[dataThing.id].heading = dataThing.heading
    state.things[dataThing.id].lastReport = dataThing.lastReport
    state.things[dataThing.id].type = dataThing.type
    state.things[dataThing.id].subType = dataThing.subType
    state.things[dataThing.id].speed = dataThing.speed
    state.things[dataThing.id].speedLastModified = dataThing.speedLastModified
    state.things[dataThing.id].services = dataThing.services
    state.things[dataThing.id].fuel_cut_off = dataThing.fuel_cut_off
    state.things[dataThing.id].contact_on = dataThing.contact_on
    state.things[dataThing.id].batteryStatus = dataThing.batteryStatus
    Vue.set(state.things[dataThing.id], 'telemetry', dataThing.telemetry)
    Vue.set(state.things[dataThing.id], 'ibutton', dataThing.ibutton)
    Vue.set(state.things[dataThing.id], 'fuel_tank', dataThing.fuel_tank)
    Vue.set(state.things[dataThing.id], 'sensors', dataThing.sensors)
    Vue.set(
      state.things[dataThing.id],
      'signalSensorTimes',
      dataThing.signalSensorTimes
    )

    if (dataThing.type == 'flex') {
      state.things[dataThing.id].batteryLevel = dataThing.batteryLevel
    }
  },
  centerOnMarker(state, { id, map, showEntityType }) {
    const position = state.things[id].marker.getPosition()
    map.setCenter(position) // setCenter takes a LatLng object
    //se agrega un zIndex de 10000 para que el marker seleccionado este por arriba de los demas
    state.things[id].marker.set('zIndex', 10000)
    //El timeout hace que despues de 30 ssegundo el marker se resetee a su zIndex original, para evitar que todos los makers esten al mismo ninvel
    //thing se genera con el "id" para respetar el parametro que espera 'mapState/resetMarker'
    const thing = { id }
    setTimeout(() => {
      store.dispatch('mapState/resetMarker', thing)
    }, 30000)

    map.setZoom(14.5)
    new RippleMarker({
      position,
      map,
      showEntityType
    })
  },
  centerOnNotification(state, { position, map, showEntityType }) {
    map.setCenter(position) // setCenter takes a LatLng object
    map.setZoom(14.5)

    if (state.pondRippleEvent !== null) state.pondRippleEvent.setMap(null)

    const pondRipple = new PondRippleEvent({
      position,
      map,
      showEntityType
    })

    state.pondRippleEvent = pondRipple
  },
  setStreetViewPanoramaElement(state, { streetViewPanoramaElement }) {
    state.streetViewPanoramaElement = streetViewPanoramaElement
  },
  setIsPanoramActive(state, { active }) {
    state.isPanoramActive = active
  },
  setDrawerComponent(state, payload) {
    state.drawerComponent = payload
  },
  refreshReferenceThingsState(state, { things }) {
    state.things = things
  },
  refreshReferenceEntitiesFilteredState(state, { entitiesFiltered }) {
    state.entitiesFiltered = entitiesFiltered
  }
}
